import React from 'react';

/**
 * Shared primitives for v2 kitchen-appliance elevation schematics.
 *
 * Coordinate convention — same as the fallback box at
 * RenderWallElevation.js:2208–2244: the parent <svg> has
 *   viewBox="0 0 widthMeters heightMeters"
 *   preserveAspectRatio="xMidYMid meet"
 * with `vectorEffect="non-scaling-stroke"` on every stroked element so
 * stroke widths stay in pixel space regardless of zoom. All children
 * here render in that same meter-space and inherit the caller's
 * `strokeColor` / `strokeWidth`.
 */

const INCHES_TO_METERS = 0.0254;
export const TOE_KICK_HEIGHT_M = 4 * INCHES_TO_METERS;
const CONTROL_BAND_RATIO = 0.12;

export function ApplianceShell({
  width,
  height,
  strokeColor,
  strokeWidth,
  children,
}) {
  return (
    <svg
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="xMidYMid meet"
      style={{ width: '100%', height: '100%', overflow: 'visible' }}
    >
      <ApplianceFrame
        width={width}
        height={height}
        strokeColor={strokeColor}
        strokeWidth={strokeWidth}
      />
      {children}
    </svg>
  );
}

export function ApplianceFrame({ width, height, strokeColor, strokeWidth }) {
  return (
    <rect
      x={0}
      y={0}
      width={width}
      height={height}
      fill="none"
      stroke={strokeColor}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      vectorEffect="non-scaling-stroke"
    />
  );
}

/**
 * Toe-kick recess line at the bottom of the schematic. Drawn as a
 * single horizontal stroke at TOE_KICK_HEIGHT_M from the bottom edge,
 * matching how real cabinets and counter-height appliances read in
 * elevation. SVG y grows downward, so y = height - TOE_KICK_HEIGHT_M.
 */
export function ToeKick({ width, height, strokeColor, strokeWidth }) {
  if (height <= TOE_KICK_HEIGHT_M) return null;
  const y = height - TOE_KICK_HEIGHT_M;
  return (
    <line
      x1={0}
      y1={y}
      x2={width}
      y2={y}
      stroke={strokeColor}
      strokeWidth={strokeWidth}
      vectorEffect="non-scaling-stroke"
    />
  );
}

/**
 * Horizontal divider that splits the body into two stacked panels
 * (e.g. control band over door, top-freezer over fridge).
 */
export function HorizontalSplit({
  width,
  y,
  strokeColor,
  strokeWidth,
  x1 = 0,
  x2 = null,
}) {
  return (
    <line
      x1={x1}
      y1={y}
      x2={x2 ?? width}
      y2={y}
      stroke={strokeColor}
      strokeWidth={strokeWidth}
      vectorEffect="non-scaling-stroke"
    />
  );
}

export function VerticalSplit({
  height,
  x,
  strokeColor,
  strokeWidth,
  y1 = 0,
  y2 = null,
}) {
  return (
    <line
      x1={x}
      y1={y1}
      x2={x}
      y2={y2 ?? height}
      stroke={strokeColor}
      strokeWidth={strokeWidth}
      vectorEffect="non-scaling-stroke"
    />
  );
}

/**
 * Bar pull / drawer pull glyph. Rendered as a short horizontal
 * stroke. `length` and `y` are in meters; the line is centered on
 * (cx, y).
 */
export function HorizontalHandle({ cx, y, length, strokeColor, strokeWidth }) {
  const half = length / 2;
  return (
    <line
      x1={cx - half}
      y1={y}
      x2={cx + half}
      y2={y}
      stroke={strokeColor}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      vectorEffect="non-scaling-stroke"
    />
  );
}

/**
 * Vertical bar pull, e.g. fridge door handle near the divider.
 */
export function VerticalHandle({ x, cy, length, strokeColor, strokeWidth }) {
  const half = length / 2;
  return (
    <line
      x1={x}
      y1={cy - half}
      x2={x}
      y2={cy + half}
      stroke={strokeColor}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      vectorEffect="non-scaling-stroke"
    />
  );
}

/**
 * Control band on top of the body (e.g. oven, range backguard-less
 * front controls). Returns y-coordinate of the band's BOTTOM edge so
 * door geometry knows where to start.
 */
export function ControlBand({
  width,
  height,
  strokeColor,
  strokeWidth,
  ratio = CONTROL_BAND_RATIO,
  knobCount = 0,
}) {
  const bandH = height * ratio;
  const cy = bandH / 2;
  const knobR = Math.min(bandH * 0.25, width * 0.012);
  return (
    <g>
      <line
        x1={0}
        y1={bandH}
        x2={width}
        y2={bandH}
        stroke={strokeColor}
        strokeWidth={strokeWidth}
        vectorEffect="non-scaling-stroke"
      />
      {knobCount > 0 &&
        Array.from({ length: knobCount }, (_, i) => {
          const cx = ((i + 1) / (knobCount + 1)) * width;
          return (
            <circle
              key={i}
              cx={cx}
              cy={cy}
              r={knobR}
              fill="none"
              stroke={strokeColor}
              strokeWidth={strokeWidth}
              vectorEffect="non-scaling-stroke"
            />
          );
        })}
    </g>
  );
}
